import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Ring Dossier admin viewer — Stage 1. Plain HTML, no styling work
// (§5 Feature 5 + §8). Two screens per §6 Screen Map:
//   Screen 1: list ideas with dossier state per ring
//   Screen 2: read one dossier (version-selectable)

function CoverageBadge({ value, onClick }) {
  if (value === null || value === undefined) return <span className="text-slate-400">—</span>;
  const pct = Math.round(value * 100);
  const color = value >= 0.95 ? "#16a34a" : value >= 0.85 ? "#d97706" : "#dc2626";
  // Clickable when onClick is provided — a raw badge is display-only, but
  // when the operator can drill into missed_terms, the coverage number
  // becomes the entry point. Craig's rule: "The number is visible and the
  // diagnosis behind it isn't." One click closes that gap.
  const style = { color };
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="mono-caps text-xs font-bold underline decoration-dotted underline-offset-2 hover:bg-slate-100 px-1 py-0.5 rounded"
        style={style}
        data-testid={`coverage-badge-${pct}`}
        title="Click to see the missed terms behind this coverage number"
      >
        {pct}%
      </button>
    );
  }
  return (
    <span
      className="mono-caps text-xs font-bold"
      style={style}
      data-testid={`coverage-badge-${pct}`}
    >
      {pct}%
    </span>
  );
}

function StatePill({ status, isStale }) {
  if (status === "none") return <span className="text-slate-400 text-xs">none</span>;
  if (isStale) {
    return (
      <span
        className="text-[0.65rem] mono-caps font-bold px-2 py-1 rounded"
        style={{ background: "#fef3c7", color: "#92400e" }}
        data-testid="dossier-stale-pill"
      >
        stale
      </span>
    );
  }
  return (
    <span
      className="text-[0.65rem] mono-caps font-bold px-2 py-1 rounded"
      style={{ background: "#dcfce7", color: "#15803d" }}
    >
      present
    </span>
  );
}

export function AdminDossierList() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  // Coverage-drill state — clicking a coverage badge fetches missed_terms
  // and opens a modal. `null` = closed; `{ideaId, ring, loading, data}`
  // when opening. Data lives on the modal, not on the row, so the list
  // doesn't have to hold 100s of missed_terms arrays it never renders.
  const [coverageDrill, setCoverageDrill] = useState(null);

  const openCoverageDrill = async (ideaId, ideaTitle, ring) => {
    setCoverageDrill({ ideaId, ideaTitle, ring, loading: true, data: null, error: null });
    try {
      const r = await api.get(`/admin/dossiers/${ideaId}/${ring}/coverage`);
      setCoverageDrill({ ideaId, ideaTitle, ring, loading: false, data: r.data, error: null });
    } catch (e) {
      setCoverageDrill({
        ideaId,
        ideaTitle,
        ring,
        loading: false,
        data: null,
        error: e?.response?.data?.detail || e?.message || "Failed to load coverage.",
      });
    }
  };

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/admin/dossiers");
      setRows(r.data.ideas || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const backfill = async (ideaId) => {
    // Instrumentation: proves the click handler fires. If Craig clicks
    // Backfill and this log does NOT appear in the browser console, the
    // onClick never reached JS (disabled state, event handler binding,
    // etc). If the log DOES appear but no network request follows, the
    // failure is between here and api.post.
    // eslint-disable-next-line no-console
    console.log("[dossier-backfill] handler entered for", ideaId, "busyId=", busyId);
    if (busyId === ideaId) {
      // eslint-disable-next-line no-console
      console.warn("[dossier-backfill] guard: already busy for this idea — click ignored");
      return;
    }
    setBusyId(ideaId);
    try {
      // eslint-disable-next-line no-console
      console.log("[dossier-backfill] posting to /admin/dossiers/" + ideaId + "/backfill");
      // POST returns 202 + job_id immediately; poll until terminal. Prod
      // sync return would 524 at Cloudflare's 100s ceiling on any dense
      // transcript (Ring 2 has 35 turns), so the polling path is the only
      // one that reflects real outcomes.
      const startResp = await api.post(`/admin/dossiers/${ideaId}/backfill`);
      const jobId = startResp.data?.job_id;
      if (!jobId) {
        toast.error("Backfill did not return a job_id — server may be running an older build.");
        return;
      }
      let final = startResp.data;
      const startedAt = Date.now();
      // Poll every 3s; hard-stop at 15 min so a stuck job doesn't hang
      // the UI forever. On a 35-turn Ring 2 we expect ~2-3 min per ring.
      while (final?.status === "running") {
        await new Promise((res) => setTimeout(res, 3000));
        if (Date.now() - startedAt > 15 * 60 * 1000) {
          toast.error(`Backfill job ${jobId} did not finish in 15 min — check server logs.`);
          return;
        }
        try {
          const poll = await api.get(`/admin/dossiers/${ideaId}/backfill/status/${jobId}`);
          final = poll.data;
        } catch (pe) {
          toast.error(`Poll failed for job ${jobId}: ${pe?.response?.data?.detail || pe?.message}`);
          return;
        }
      }
      // eslint-disable-next-line no-console
      console.log("[dossier-backfill] terminal:", final);
      const rings = final?.rings || {};
      // Build a compact readable summary — every ring names its outcome,
      // no "skipped" without a reason. If any ring skipped, the toast is
      // an error toast so it doesn't visually read as success.
      const parts = [];
      let anyWritten = false;
      let anySkipped = false;
      for (const [ring, info] of Object.entries(rings)) {
        if (info.status === "written") {
          anyWritten = true;
          parts.push(`r${ring} written (coverage ${Math.round((info.coverage || 0) * 100)}%)`);
        } else {
          anySkipped = true;
          parts.push(`r${ring} ${info.status}: ${info.reason || "unknown"}`);
        }
      }
      const summary = parts.join(" · ");
      if (anySkipped && !anyWritten) {
        toast.error(`Backfill: ${summary}`, { duration: 12000 });
      } else if (anySkipped) {
        toast.warning(`Backfill (partial): ${summary}`, { duration: 12000 });
      } else {
        toast.success(`Backfill: ${summary}`, { duration: 8000 });
      }
      // Also surface the details inline in the row via a sticky console-
      // like note keyed on idea — one screen for cause AND state.
      const details = Object.entries(rings)
        .filter(([, info]) => info.status !== "written")
        .map(([ring, info]) => `Ring ${ring} · ${info.status}: ${info.reason || ""}\n${info.detail || "(no detail)"}`)
        .join("\n\n");
      if (details) {
        // eslint-disable-next-line no-alert
        console.warn("[dossier-backfill]", ideaId, "\n" + details);
      }
      await refresh();
    } catch (e) {
      toast.error(`Backfill request failed: ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const diagnose = async (ideaId) => {
    try {
      const r = await api.get(`/admin/dossiers/${ideaId}/diagnostic`);
      const d = r.data;
      // eslint-disable-next-line no-console
      console.info("[dossier-diagnostic]", d);
      const rings = d.per_ring || {};
      const ringSummary = [1, 2, 3]
        .map((n) => `r${n}: ${rings[`ring${n}`]?.founder_turns || 0} turns`)
        .join(" · ");
      const llmOk = d.checks?.llm_key_configured ? "llm_key ✓" : "llm_key MISSING";
      toast.info(
        `Diag ${ideaId.slice(0, 8)} · ${d.checks?.total_messages || 0} msgs · ${ringSummary} · ${llmOk}`,
        { duration: 15000 }
      );
    } catch (e) {
      toast.error(`Diagnostic failed: ${e?.response?.data?.detail || e?.message}`);
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-8 space-y-6" data-testid="admin-dossiers-page">
      <div>
        <div className="mono-caps text-xs text-slate-500">Stage 1 · Ring Dossier layer</div>
        <h1 className="text-3xl font-semibold">Dossiers</h1>
        <p className="text-slate-600 text-sm mt-1">
          One row per idea. Coverage renders per ring. Green ≥ 95%, amber ≥ 85%, red below.
          Backfill runs sync — may take up to ~45s per idea.
        </p>
      </div>

      {busyId && (
        <div className="text-xs text-slate-500 flex items-center gap-3">
          <span>Currently backfilling <span className="font-mono">{busyId}</span>…</span>
          <button
            type="button"
            onClick={() => setBusyId(null)}
            className="mono-caps text-[0.65rem] px-2 py-1 rounded border border-slate-400 hover:bg-slate-100"
            data-testid="dossier-reset-stuck-state-btn"
            title="Clears the local busy flag if a prior click got stuck. Does NOT cancel any in-flight backend work."
          >
            Reset stuck state
          </button>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border-2 border-red-500 rounded p-3 text-sm text-red-900" data-testid="admin-dossiers-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-slate-500">Loading…</div>
      ) : (
        <table className="w-full border-collapse text-sm" data-testid="admin-dossiers-table">
          <thead>
            <tr className="text-left border-b-2 border-slate-300">
              <th className="p-2">Idea</th>
              <th className="p-2 text-center">Ring 1</th>
              <th className="p-2 text-center">Ring 2</th>
              <th className="p-2 text-center">Ring 3</th>
              <th className="p-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((idea) => (
              <tr key={idea.id} className="border-b border-slate-200 hover:bg-slate-50" data-testid={`dossier-row-${idea.id}`}>
                <td className="p-2">
                  <div className="font-medium">{idea.title || "(untitled)"}</div>
                  <div className="text-xs text-slate-500 font-mono">{idea.id}</div>
                </td>
                {[1, 2, 3].map((r) => {
                  const cell = idea.dossiers[`ring${r}`] || {};
                  return (
                    <td key={r} className="p-2 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <StatePill status={cell.status} isStale={cell.is_stale} />
                        <CoverageBadge
                          value={cell.coverage}
                          onClick={
                            cell.coverage !== null && cell.coverage !== undefined
                              ? () => openCoverageDrill(idea.id, idea.title, r)
                              : undefined
                          }
                        />
                      </div>
                      {cell.versions_count > 1 && (
                        <div className="text-[0.65rem] text-slate-400 mt-1">v{cell.version} · {cell.versions_count} versions</div>
                      )}
                    </td>
                  );
                })}
                <td className="p-2 text-right space-x-2">
                  <button
                    onClick={() => nav(`/admin/dossiers/${idea.id}`)}
                    className="px-3 py-1 text-xs rounded border border-slate-300 hover:bg-slate-100"
                    data-testid={`dossier-open-${idea.id}`}
                  >
                    Open
                  </button>
                  <button
                    onClick={() => diagnose(idea.id)}
                    className="px-3 py-1 text-xs rounded border border-slate-400 hover:bg-slate-100"
                    data-testid={`dossier-diagnose-${idea.id}`}
                    title="Dry-run: reports preconditions without calling the LLM"
                  >
                    Diagnose
                  </button>
                  <button
                    onClick={() => backfill(idea.id)}
                    className={
                      "px-3 py-1 text-xs rounded text-white " +
                      (busyId === idea.id
                        ? "bg-slate-500 cursor-wait"
                        : "bg-slate-800 hover:bg-slate-700")
                    }
                    data-testid={`dossier-backfill-${idea.id}`}
                    title={busyId === idea.id
                      ? "This idea is currently being backfilled. If it stuck, click Reset stuck state."
                      : "Generate dossiers for rings 1, 2, and 3 sequentially. ~45-120s per idea."}
                  >
                    {busyId === idea.id ? "Backfilling…" : "Backfill"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Coverage drill — missed_terms + block_sizes for one (idea, ring).
          The number is on the row; the diagnosis is one click away. */}
      <Dialog
        open={!!coverageDrill}
        onOpenChange={(v) => { if (!v) setCoverageDrill(null); }}
      >
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="coverage-drill-modal">
          <DialogHeader>
            <DialogTitle>
              Coverage diagnostic
              {coverageDrill?.data && (
                <span className="ml-2 text-xs font-normal text-slate-500">
                  Ring {coverageDrill.ring} · v{coverageDrill.data.version} · {coverageDrill.data.coverage_pct}%
                </span>
              )}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {coverageDrill?.ideaTitle || coverageDrill?.ideaId}
            </DialogDescription>
          </DialogHeader>
          {coverageDrill?.loading && (
            <div className="text-sm text-slate-500 py-8 text-center">Loading…</div>
          )}
          {coverageDrill?.error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
              {coverageDrill.error}
            </div>
          )}
          {coverageDrill?.data && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="bg-slate-50 border border-slate-200 rounded p-2">
                  <div className="text-slate-500 mono-caps">Extracted</div>
                  <div className="font-mono text-lg">{coverageDrill.data.extracted_terms_count}</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded p-2">
                  <div className="text-slate-500 mono-caps">Missed</div>
                  <div className="font-mono text-lg text-red-700">{coverageDrill.data.missed_count}</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded p-2">
                  <div className="text-slate-500 mono-caps">Source msgs</div>
                  <div className="font-mono">{coverageDrill.data.source_message_count}</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded p-2">
                  <div className="text-slate-500 mono-caps">Source chars</div>
                  <div className="font-mono">{coverageDrill.data.source_transcript_chars}</div>
                </div>
              </div>

              <div>
                <div className="mono-caps text-xs text-slate-500 mb-1">Block sizes</div>
                <div className="grid grid-cols-5 gap-2 text-xs">
                  {Object.entries(coverageDrill.data.block_sizes || {}).map(([k, v]) => (
                    <div key={k} className="bg-white border border-slate-200 rounded p-2 text-center">
                      <div className="text-slate-500 text-[0.65rem] mono-caps">{k}</div>
                      <div className="font-mono">{v}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="mono-caps text-xs text-slate-500 mb-1">
                  Missed terms ({coverageDrill.data.missed_count})
                </div>
                {coverageDrill.data.missed_count === 0 ? (
                  <div className="text-xs text-slate-500 italic">None — everything the extractor found is present in the dossier.</div>
                ) : (
                  <div
                    className="max-h-64 overflow-y-auto bg-slate-50 border border-slate-200 rounded p-2 font-mono text-xs"
                    data-testid="coverage-drill-missed-terms"
                  >
                    {coverageDrill.data.missed_terms.map((t, i) => (
                      <div key={i} className="py-0.5 border-b border-slate-100 last:border-b-0">{t}</div>
                    ))}
                  </div>
                )}
              </div>

              <div className="text-[0.7rem] text-slate-500 border-t border-slate-200 pt-2 leading-relaxed">
                Coverage = extracted_terms_present_in_dossier / extracted_terms_total. Extraction runs a
                regex over founder turns for proper nouns + numeric expressions; a term is "present" if its
                exact substring appears in the flattened dossier text. False-positive misses (matcher scaffolding)
                and true-positive misses (substance drop) both land here — read the list before concluding
                which one this is.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function AdminDossierDetail() {
  const { ideaId } = useParams();
  const nav = useNavigate();
  const [index, setIndex] = useState(null);
  const [selected, setSelected] = useState({ ring: null, version: null });
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenBusy, setRegenBusy] = useState(null);

  const loadIndex = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/dossiers/${ideaId}`);
      setIndex(r.data);
      const first = (r.data.dossiers_by_ring || []).find((row) => row.versions.length);
      if (first) {
        setSelected({ ring: first.ring, version: first.versions[0].version });
      }
    } catch (e) {
      toast.error(`Load failed: ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadIndex(); }, [ideaId]);

  useEffect(() => {
    if (!selected.ring || !selected.version) { setDossier(null); return; }
    (async () => {
      try {
        const r = await api.get(`/admin/dossiers/${ideaId}/${selected.ring}/${selected.version}`);
        setDossier(r.data);
      } catch (e) {
        toast.error(`Fetch dossier failed: ${e?.response?.data?.detail || e?.message}`);
      }
    })();
  }, [ideaId, selected.ring, selected.version]);

  const regenerate = async (ring) => {
    setRegenBusy(ring);
    try {
      // POST returns 202 + job_id immediately; poll until terminal. Sync
      // return would 524 at Cloudflare's edge on any dense transcript.
      const startResp = await api.post(`/admin/dossiers/${ideaId}/${ring}/regenerate`);
      const jobId = startResp.data?.job_id;
      if (!jobId) {
        toast.error("Regenerate did not return a job_id — server may be running an older build.");
        return;
      }
      let final = startResp.data;
      const startedAt = Date.now();
      while (final?.status === "running") {
        await new Promise((res) => setTimeout(res, 3000));
        if (Date.now() - startedAt > 10 * 60 * 1000) {
          toast.error(`Regenerate job ${jobId} did not finish in 10 min — check server logs.`);
          return;
        }
        try {
          const poll = await api.get(`/admin/dossiers/${ideaId}/${ring}/regenerate/status/${jobId}`);
          final = poll.data;
        } catch (pe) {
          toast.error(`Poll failed for job ${jobId}: ${pe?.response?.data?.detail || pe?.message}`);
          return;
        }
      }
      if (final?.status === "written") {
        toast.success(`Regenerated ring ${ring} v${final.version} · coverage ${Math.round((final.coverage || 0) * 100)}%`);
      } else {
        toast.info(`${final?.status || "unknown"}: ${final?.reason || "no reason"} — ${final?.detail || ""}`, { duration: 12000 });
      }
      await loadIndex();
    } catch (e) {
      toast.error(`Regenerate failed: ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setRegenBusy(null);
    }
  };

  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;
  if (!index) return <div className="p-8 text-red-600">Failed to load index.</div>;

  return (
    <div className="mx-auto max-w-5xl p-8 space-y-6" data-testid="admin-dossier-detail-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <button onClick={() => nav("/admin/dossiers")} className="mono-caps text-xs text-slate-500 hover:text-slate-900 mb-2">
            ← Back to dossiers
          </button>
          <h1 className="text-2xl font-semibold">{index.idea.title}</h1>
          <div className="text-xs text-slate-500 font-mono mt-1">{index.idea.id}</div>
          <div className="text-xs text-slate-500 mt-1">
            Current ring: {index.idea.current_ring} · Status: {index.idea.status}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {[1, 2, 3].map((ring) => {
          const rowIdx = (index.dossiers_by_ring || []).find((r) => r.ring === ring);
          const transcript = index.transcript_state[`ring${ring}`];
          const versions = rowIdx?.versions || [];
          return (
            <div key={ring} className="border border-slate-300 rounded p-4" data-testid={`ring-panel-${ring}`}>
              <div className="flex items-center justify-between">
                <div className="font-semibold">Ring {ring}</div>
                <button
                  onClick={() => regenerate(ring)}
                  disabled={regenBusy === ring || !transcript.has_transcript}
                  title={!transcript.has_transcript ? "No transcript for this ring" : "Create version n+1"}
                  className="text-xs px-2 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40"
                  data-testid={`regen-ring-${ring}`}
                >
                  {regenBusy === ring ? "…" : "Regenerate"}
                </button>
              </div>
              <div className="text-xs text-slate-500 mt-2">
                {transcript.founder_turns} founder turns · {transcript.founder_chars} chars
              </div>
              {versions.length === 0 ? (
                <div className="mt-3 text-xs text-slate-400" data-testid={`ring-${ring}-no-dossier`}>
                  {transcript.has_transcript
                    ? "No dossier yet — backfill or regenerate to create one."
                    : "No transcript — dossier cannot be generated."}
                </div>
              ) : (
                <div className="mt-3 space-y-1">
                  {versions.map((v) => (
                    <button
                      key={v.version}
                      onClick={() => setSelected({ ring, version: v.version })}
                      className={`w-full text-left text-xs px-2 py-1 rounded border ${
                        selected.ring === ring && selected.version === v.version
                          ? "bg-slate-800 text-white border-slate-800"
                          : "border-slate-200 hover:bg-slate-50"
                      }`}
                      data-testid={`version-btn-${ring}-${v.version}`}
                    >
                      v{v.version} · <CoverageBadge value={v.dossier_coverage} />
                      {v.is_stale && <span className="ml-2 text-amber-700 font-bold">stale</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {dossier && <DossierRender d={dossier} />}
    </div>
  );
}

function DossierRender({ d }) {
  return (
    <div className="border-2 border-slate-300 rounded-xl p-6 space-y-6 bg-white" data-testid="dossier-render">
      {d.is_stale && (
        <div className="bg-amber-50 border-l-4 border-amber-500 p-3 text-sm text-amber-900" data-testid="dossier-stale-banner">
          <b>This dossier is stale.</b> The ring has been re-scored since it was written. Reason: {d.stale_reason}.
          Regenerate to produce a new version.
        </div>
      )}

      <div>
        <div className="mono-caps text-xs text-slate-500">Block 1 · Header</div>
        <h2 className="text-xl font-semibold" data-testid="dossier-block-1">
          {d.ring_name} · {d.idea_title}
        </h2>
        <div className="text-sm text-slate-500">
          Score {d.ring_score ?? "—"} · Signal {d.ring_signal ?? "—"} · v{d.version} · generated {d.generated_at}
        </div>
        <div className="text-xs text-slate-400 mt-1">
          Coverage: <CoverageBadge value={d.dossier_coverage} /> ·{" "}
          {d.dossier_extracted_terms_count} terms extracted ·{" "}
          {(d.dossier_missed_terms || []).length} missed
        </div>
        {(d.dossier_missed_terms || []).length > 0 && (
          <div className="mt-2 text-xs text-slate-500" data-testid="dossier-missed-terms">
            <b>Missed terms:</b> {d.dossier_missed_terms.join(", ")}
          </div>
        )}
      </div>

      <Block title="Block 2 · What You Established" testid="dossier-block-2">
        {(d.findings || []).map((f, i) => (
          <div key={i} className="mb-3">
            <b>{f.label}:</b> {f.body}
          </div>
        ))}
      </Block>

      <Block title="Block 3 · In Your Words" testid="dossier-block-3">
        {(d.quotes || []).map((q, i) => (
          <blockquote key={i} className="border-l-4 border-slate-300 pl-4 py-1 mb-3 text-slate-700">
            "{q.text}"
            <div className="text-xs text-slate-400 mt-1">
              {q.unmapped ? <span className="text-amber-700">unmapped</span> : q.criterion_id}
            </div>
          </blockquote>
        ))}
      </Block>

      <Block title="Block 4 · The Numbers On Record" testid="dossier-block-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-slate-300">
              <th className="p-2">Figure</th>
              <th className="p-2">Measures</th>
              <th className="p-2">Founder framing</th>
              <th className="p-2">Qualifier</th>
            </tr>
          </thead>
          <tbody>
            {(d.numbers || []).map((n, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="p-2 font-mono">{n.figure}</td>
                <td className="p-2">{n.measures}</td>
                <td className="p-2 text-slate-600">{n.founder_framing}</td>
                <td className="p-2">
                  <span className={n.qualifier === "verified" ? "text-green-700" : "text-amber-700"}>
                    {n.qualifier}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Block>

      <Block title="Block 5 · Named In This Ring" testid="dossier-block-5">
        <ul className="list-disc pl-6 space-y-1">
          {(d.named_entities || []).map((e, i) => (
            <li key={i}>
              <b>{e.name}</b> <span className="text-xs text-slate-500">({e.kind})</span> — {e.context}
            </li>
          ))}
        </ul>
      </Block>

      <Block title="Block 6 · What This Unlocked" testid="dossier-block-6">
        {(d.unlocked || []).map((u, i) => (
          <div key={i} className="mb-3">
            <b>{u.label}:</b> {u.body}
          </div>
        ))}
      </Block>

      <Block title="Block 7 · Not Directly Probed" testid="dossier-block-7">
        {(d.not_directly_probed || []).length === 0 ? (
          <div className="text-slate-400 text-sm">Every criterion in this ring was directly probed.</div>
        ) : (
          <>
            <div className="italic text-slate-600 mb-2">
              Not directly probed, inferred from adjacent answers:
            </div>
            <div className="text-sm">
              {(d.not_directly_probed || []).map((c) => (
                <span key={c} className="inline-block mr-2 mb-1 px-2 py-1 rounded bg-slate-100 font-mono text-xs">{c}</span>
              ))}
            </div>
          </>
        )}
      </Block>

      {(d.next_ring_questions || []).length > 0 && (
        <Block title={`Block 8 · What Ring ${d.ring_number + 1} Will Press On`} testid="dossier-block-8">
          <ul className="list-disc pl-6 space-y-2">
            {d.next_ring_questions.map((q, i) => (
              <li key={i}>
                <div>{q.question}</div>
                {q.context && <div className="text-xs text-slate-500 mt-1">{q.context}</div>}
              </li>
            ))}
          </ul>
        </Block>
      )}
    </div>
  );
}

function Block({ title, testid, children }) {
  return (
    <div data-testid={testid}>
      <div className="mono-caps text-xs text-slate-500 mb-2">{title}</div>
      <div>{children}</div>
    </div>
  );
}
